import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Play } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { inr } from '../lib/currency';
import { opt, srcset } from '../lib/media';

export default function ProductCard({ product, index = 0 }) {
  const { toggleWishlist, inWishlist } = useCart();
  const wished = inWishlist(product.id);
  const hasSecond = product.images && product.images.length > 1;
  const video = product.videos && product.videos.length ? product.videos[0] : null;
  const vidRef = useRef(null);

  const onEnter = () => { if (vidRef.current) { vidRef.current.play().catch(() => {}); } };
  const onLeave = () => { if (vidRef.current) { vidRef.current.pause(); vidRef.current.currentTime = 0; } };

  return (
    <div className="group fade-up" style={{ animationDelay: `${(index % 4) * 0.08}s` }}>
      <Link to={`/product/${product.id}`} className="block">
        <div className="img-zoom relative aspect-[3/4] bg-cream overflow-hidden" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          {video ? (
            <video
              ref={vidRef}
              src={video}
              poster={opt(product.images && product.images[0], 800)}
              className="w-full h-full object-cover"
              muted loop playsInline preload="none"
            />
          ) : (
            <>
              <img src={opt(product.images[0], 800)} srcSet={srcset(product.images[0])} sizes="(min-width: 1024px) 25vw, 50vw" alt={product.name} loading="lazy" decoding="async" width="600" height="800" className="w-full h-full object-cover" />
              {hasSecond && (
                <img src={opt(product.images[1], 800)} srcSet={srcset(product.images[1])} sizes="(min-width: 1024px) 25vw, 50vw" alt={product.name} loading="lazy" decoding="async" width="600" height="800" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              )}
            </>
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.isNew && <span className="bg-burgundy-900 text-cream text-[10px] tracking-wideluxe uppercase px-2.5 py-1">New</span>}
            {product.readyToShip && <span className="bg-gold text-burgundy-950 text-[10px] tracking-wideluxe uppercase px-2.5 py-1">Ready To Ship</span>}
          </div>
          {video && (
            <span className="absolute bottom-3 left-3 flex items-center gap-1 bg-burgundy-950/70 backdrop-blur text-cream text-[10px] tracking-wideluxe uppercase px-2.5 py-1">
              <Play size={11} className="fill-cream" /> Reel
            </span>
          )}
          <button
            onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-cream/80 backdrop-blur flex items-center justify-center hover:bg-cream transition-colors"
            aria-label="Wishlist"
          >
            <Heart size={16} className={wished ? 'fill-burgundy-800 text-burgundy-800' : 'text-burgundy-900'} />
          </button>
        </div>
      </Link>
      <div className="pt-4 text-center">
        <p className="text-[10px] tracking-luxe uppercase text-gold-dark mb-1.5">HITÉRA</p>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display text-lg text-burgundy-900 leading-tight hover:text-gold-dark transition-colors">{product.name}</h3>
        </Link>
        <div className="mt-2 flex items-center justify-center gap-2">
          <span className="text-sm text-burgundy-800">{inr(product.price)}</span>
          {product.originalPrice && <span className="text-xs text-burgundy-900/40 line-through">{inr(product.originalPrice)}</span>}
        </div>
      </div>
    </div>
  );
}
